<template>
  <v-card class="default-card pa-6" elevation="3">
    <v-card-title class="toolbox-card-title">
      {{ $t('functionalities.projectInfo.name') }}
    </v-card-title>
    <v-row>
      <v-col cols="12">
        <v-card-text>
          <v-form @submit.prevent="saveProjectInfo">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.name"
                  :label="$t('components.projectInfo.projectName')"
                  variant="outlined"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.clientName"
                  :label="$t('components.projectInfo.clientName')"
                  variant="outlined"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="form.number"
                  :label="$t('components.projectInfo.number')"
                  variant="outlined"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.description"
                  :label="$t('components.projectInfo.description')"
                  variant="outlined"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.constructionStartDate"
                  :label="$t('components.projectInfo.constructionStartDate')"
                  type="month"
                  variant="outlined"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="12" md="12">
                {{ capitalize($t('address')) }}
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.location.number"
                  :label="$t('components.projectInfo.streetNumber')"
                  variant="outlined"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.location.street"
                  :label="$t('components.projectInfo.street')"
                  variant="outlined"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.location.city"
                  :label="$t('components.projectInfo.city')"
                  variant="outlined"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.location.province"
                  :label="$t('components.projectInfo.province')"
                  variant="outlined"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.location.postalCode"
                  :label="$t('components.projectInfo.postalCode')"
                  variant="outlined"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.location.country"
                  :label="$t('components.projectInfo.country')"
                  variant="outlined"
                  density="comfortable"
                />
              </v-col>
            </v-row>

            <div class="project-info-actions">
              <v-btn color="secondary" variant="flat" type="submit" :loading="isSaving">
                {{ $t('components.projectInfo.save') }}
              </v-btn>
              <span v-if="saveState === 'success'" class="project-info-feedback success">
                {{ $t('components.projectInfo.saveSuccess') }}
              </span>
              <span v-if="saveState === 'error'" class="project-info-feedback error">
                {{ $t('components.projectInfo.saveError') }}
              </span>
            </div>
          </v-form>
        </v-card-text>
      </v-col>
    </v-row>
  </v-card>
</template>

<script setup>
import { computed, reactive, ref, watch, capitalize } from "vue";
import { useStore } from "vuex";

const store = useStore();
const isSaving = ref(false);
const saveState = ref(null);

const projectInfo = computed(() => store.state.projectInfo ?? {});

const form = reactive({
  name: "",
  number: "",
  description: "",
  clientName: "",
  constructionStartDate: "",
  location: {
    postalCode: "",
    street: "",
    number: "",
    city: "",
    province: "",
    country: "",
  },
});

const fillForm = (source) => {
  form.name = source?.name ?? "";
  form.number = source?.number ?? "";
  form.description = source?.description ?? "";
  form.clientName = source?.clientName ?? "";
  form.constructionStartDate = source?.constructionStartDate ?? "";
  form.location.postalCode = source?.location?.postalCode ?? "";
  form.location.street = source?.location?.street ?? "";
  form.location.number = source?.location?.number ?? "";
  form.location.city = source?.location?.city ?? "";
  form.location.province = source?.location?.province ?? "";
  form.location.country = source?.location?.country ?? "";
};

watch(
  projectInfo,
  (value) => {
    fillForm(value);
  },
  { immediate: true, deep: true }
);

const normalizeValue = (value) => {
  if (value === "" || value === undefined) {
    return null;
  }
  return value;
};

const saveProjectInfo = async () => {
  try {
    isSaving.value = true;
    saveState.value = null;
    await store.dispatch("saveProjectInfo", {
      name: normalizeValue(form.name),
      number: normalizeValue(form.number),
      description: normalizeValue(form.description),
      clientName: normalizeValue(form.clientName),
      constructionStartDate: normalizeValue(form.constructionStartDate),
      location: {
        postalCode: normalizeValue(form.location.postalCode),
        street: normalizeValue(form.location.street),
        number: normalizeValue(form.location.number),
        city: normalizeValue(form.location.city),
        province: normalizeValue(form.location.province),
        country: normalizeValue(form.location.country),
      },
    });
    saveState.value = "success";
  } catch (error) {
    console.error("Failed to save project info:", error);
    saveState.value = "error";
  } finally {
    isSaving.value = false;
  }
};
</script>

<style scoped>
@import '@/assets/styles/components/ProjectInfo.css';
</style>